import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Info, ArrowRight, Check } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { formatSwissPhone, isValidSwissPhone, isValidEmail } from "../lib/formHelpers";
import { ChampNaissance } from "../components/ChampNaissance";
import { thumb, fallbackToFull } from "../lib/imageThumb";
import { EmailSuggestion } from "../components/EmailSuggestion";


const API = process.env.REACT_APP_BACKEND_URL;

const TABLES = [
  {
    title: "Cours de danse — Enfants",
    price: "CHF 780.–",
    rows: [
      { label: "Contribution assurance complémentaire Completa Forte *", value: "CHF 300.–" },
      { label: "Contribution assurance complémentaire Praevita **",       value: "CHF 240.–" },
      { label: "Contribution assurance complémentaire Optima ***",         value: "CHF 216.–" },
    ],
    remaining: "CHF 24.–",
  },
  {
    title: "Cours de danse — Adulte",
    price: "CHF 900.–",
    rows: [
      { label: "Contribution assurance complémentaire Completa Forte *", value: "CHF 300.–" },
      { label: "Contribution assurance complémentaire Praevita **",       value: "CHF 300.–" },
      { label: "Contribution assurance complémentaire Optima ***",         value: "CHF 270.–" },
    ],
    remaining: "CHF 30.–",
  },
];

const NOTES = [
  "* 90 % des frais jusqu'à concurrence de CHF 300.– au titre de Completa Forte (jusqu'à CHF 300.– par genre de prévention)",
  "** 50 % des frais jusqu'à concurrence de CHF 300.– au titre de Praevita (jusqu'à CHF 300.– par genre de prévention)",
  "*** 90 % des frais restants jusqu'à concurrence de CHF 300.– au titre d'Optima",
];

export default function Assurances() {
  useSEO({
    title: "Assurance & remboursement des cours — 1000 Arts x SWICA",
    description: "Jusqu'à 97 % de tes cours de danse remboursés par ton assurance complémentaire SWICA (Completa Forte, Praevita, Optima). Contacte Xavier Caruso & Gérald Cudré-Mauroux pour un accompagnement gratuit.",
    canonical: "/assurances",
  });

  const [form, setForm] = useState({ nom: "", prenom: "", dob: "", tel: "", email: "", consent: false });
  const [status, setStatus] = useState({ loading: false, ok: null, msg: "" });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: k === "tel" ? formatSwissPhone(v) : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ loading: false, ok: false, msg: "Merci d'accepter la transmission des données à Xavier Caruso & Gérald Cudré-Mauroux (SWICA)." });
      return;
    }
    if (!isValidSwissPhone(form.tel)) {
      setStatus({ loading: false, ok: false, msg: "Merci d'entrer un numéro de téléphone suisse valide (ex : +41 79 950 40 31)." });
      return;
    }
    if (!isValidEmail(form.email)) {
      setStatus({ loading: false, ok: false, msg: "Merci de vérifier ton adresse e-mail (ex : prenom.nom@exemple.ch)." });
      return;
    }
    setStatus({ loading: true, ok: null, msg: "" });
    try {
      const res = await fetch(`${API}/api/inscriptions/swica`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prenom: form.prenom,
          nom: form.nom,
          dob: form.dob,
          tel: form.tel,
          email: form.email,
          consent: form.consent,
        }),
      });
      if (!res.ok) throw new Error("Erreur envoi");
      setStatus({ loading: false, ok: true, msg: "Merci ! Tes informations ont été transmises. Tu seras contacté·e dans les 72h." });
      setForm({ nom: "", prenom: "", dob: "", tel: "", email: "", consent: false });
    } catch (err) {
      setStatus({ loading: false, ok: false, msg: "Une erreur est survenue. Contacte directement info1000arts@gmail.com." });
    }
  };

  return (
    <div data-testid="page-assurances" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 relative z-10">
      {/* HERO */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="eyebrow">Assurances</div>
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
          style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
        >
          Partenaire officiel
        </div>
      </div>
      <div className="flex flex-wrap items-start gap-8 mb-2">
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight max-w-4xl">
            Tes cours remboursés{" "}<br />
            <span style={{ color: "var(--primary)" }}>jusqu'à 97 %</span> par ton assurance
          </h1>
        </div>
        <a
          href="https://www.swica.ch"
          target="_blank"
          rel="noreferrer"
          data-testid="swica-logo-link"
          className="shrink-0 self-start block hover:scale-[1.03] transition-transform"
          aria-label="SWICA — Site officiel"
        >
          <img
            src={thumb("/img/93dd769b76.webp")} onError={fallbackToFull("/img/93dd769b76.webp")}
            alt="Logo SWICA — assurance santé partenaire de 1000 Arts"
            className="h-14 md:h-20 w-auto"
            loading="lazy"
          />
        </a>
      </div>
      <p className="mt-6 text-lg text-[var(--text)] leading-relaxed max-w-3xl">
        Si toi aussi tu souhaites payer moins cher ton abonnement chez 1000 Arts, contacte nos partenaires de confiance <strong>Xavier Caruso &amp; Gérald Cudré-Mauroux</strong>, qui se feront un plaisir de te renseigner et de t'accompagner dans les démarches. <strong>Un cadeau t'est offert par SWICA</strong> lors du rendez-vous.
      </p>

      {/* TABLES DE SIMULATION */}
      <section className="mt-16 grid md:grid-cols-2 gap-6" data-testid="swica-tables">
        {TABLES.map((t, i) => (
          <div key={i} data-testid={`table-${i}`} className="rounded-3xl border border-[var(--border-soft)] bg-white overflow-hidden">
            <div className="p-6 border-b border-[var(--border-soft)]" style={{ background: "var(--primary-tint)" }}>
              <div className="text-sm text-[var(--muted)]">{t.title}</div>
              <div className="font-display text-3xl mt-1" style={{ color: "var(--primary)" }}>{t.price}</div>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {t.rows.map((r, j) => (
                  <li key={j} className="flex items-start justify-between gap-4 text-sm">
                    <span className="text-[var(--text)] flex-1">{r.label}</span>
                    <span className="font-medium shrink-0">−&nbsp;{r.value}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t-2 border-[var(--primary)] flex items-center justify-between">
                <span className="font-semibold">Reste à ta charge</span>
                <span className="font-display text-2xl" style={{ color: "var(--primary)" }}>{t.remaining}</span>
              </div>
            </div>
          </div>
        ))}
      </section>

      <div className="mt-6 text-xs text-[var(--muted)] space-y-1" data-testid="swica-notes">
        {NOTES.map((n, i) => <p key={i}>{n}</p>)}
      </div>

      {/* FORMULAIRE DE CONTACT SWICA */}
      <section className="mt-16 rounded-3xl border-2 border-[var(--primary)] p-8 md:p-10" data-testid="swica-form-section">
        <h2 className="font-display text-3xl tracking-tight">Être contacté·e sous 72 h</h2>
        <p className="mt-3 text-[var(--muted)]">
          Remplis tes informations et tu seras contacté·e dans les 72 h. Grâce à ton contact, tu contribues aussi à l'évolution de notre école de danse. Un grand merci pour ton soutien — on se retrouve bientôt sur le dancefloor.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="swica-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Input label="Prénom" value={form.prenom} onChange={(v) => update("prenom", v)} required testid="swica-prenom" />
            <Input label="Nom" value={form.nom} onChange={(v) => update("nom", v)} required testid="swica-nom" />
            <div>
              <span className="block text-xs font-semibold uppercase tracking-wider text-[var(--muted)] mb-1.5">Date de naissance<span style={{ color: "var(--primary)" }}> *</span></span>
              <ChampNaissance value={form.dob} onChange={(v) => update("dob", v)} required testid="swica-dob"
                selectClassName="w-full py-3 rounded-xl border border-[var(--border-soft)] bg-white focus:outline-none focus:border-[var(--primary)]"
                selectStyle={{ minHeight: "calc(1.5em + 26px)" }} />
            </div>
            <Input label="N° de téléphone portable" type="tel" value={form.tel} onChange={(v) => update("tel", v)} required testid="swica-tel" inputMode="tel" placeholder="+41 79 950 40 31" />
            <div className="md:col-span-2">
              <Input label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} required testid="swica-email" />
              <EmailSuggestion value={form.email} onFix={(v) => update("email", v)} testid="swica-email-suggestion" />
            </div>
          </div>

          {/* Consentement LPD/RGPD */}
          <label className="flex items-start gap-3 p-4 rounded-2xl border border-[var(--border-soft)] cursor-pointer" data-testid="swica-consent-box">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              className="mt-1"
              data-testid="swica-consent-checkbox"
            />
            <span className="text-sm text-[var(--text)]">
              <strong>J'accepte que mes coordonnées soient transmises à Xavier Caruso &amp; Gérald Cudré-Mauroux (SWICA)</strong> afin d'être recontacté·e. Conforme LPD / RGPD.
            </span>
          </label>

          {status.msg && (
            <div
              className={`p-4 rounded-2xl text-sm ${status.ok ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
              data-testid="swica-status"
            >
              {status.msg}
            </div>
          )}

          <button type="submit" disabled={status.loading} className="btn-primary" style={{ padding: "14px 26px" }} data-testid="swica-submit">
            {status.loading ? "Envoi…" : "Envoyer mes coordonnées"} <ArrowRight size={16} />
          </button>
        </form>
      </section>

      {/* DISCLAIMER */}
      <section className="mt-10 rounded-2xl p-6 flex items-start gap-4" style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316", color: "#7C2D12" }} data-testid="swica-disclaimer">
        <Info size={22} className="shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <strong className="block mb-2">Important</strong>
          Les montants ci-dessus sont des exemples de calcul. Le remboursement effectif dépend de votre contrat d'assurance complémentaire et de votre situation personnelle. 1000 Arts n'est pas un intermédiaire d'assurance et ne garantit aucun remboursement. Lien&nbsp;: <Link to="/contact" className="underline">politique de confidentialité</Link>.
        </div>
      </section>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid, className = "", min, max, inputMode, placeholder }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold uppercase tracking-wider text-[var(--muted)] mb-1.5">{label}{required && <span style={{ color: "var(--primary)" }}> *</span>}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        min={min}
        max={max}
        inputMode={inputMode}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border border-[var(--border-soft)] bg-white focus:outline-none focus:border-[var(--primary)]"
      />
    </label>
  );
}
